import client, { replaceURLParams } from "@/utils/http";
import { Connector, Snapshot } from "@ticketmachine/sdk";
import { Readable } from "svelte/store";
import type { RawEventData } from "./resolve";
import { URLS } from "./urls";
import { decode as decodeb64 } from "base64-arraybuffer";
import { organizer_id } from "env:organizer_info";

interface WizardStart {
    id: string,
    internal: boolean,
    publicKey: string,
    iframe_url: string
}

interface WizardFunctions {
    openWizard: Connector["openWizard"]
}


export default function createWizardConnector(snapshot: Readable<Snapshot>, updateCart: () => Promise<void>, connector: () => Connector, urls: URLS): WizardFunctions {
    return {
        async* openWizard(id: string, invocation?: string) {
            const start: WizardStart = (await client.get(replaceURLParams(urls.BundleStartURL, {id}))).data;

            const url = new URL(start.iframe_url);
            const params = url.searchParams;
            params.set("invocation", invocation || "");
            params.set("oid", ""+organizer_id);
            url.search = params.toString();

            const config: {type: string} = yield {type: "configuration", internal: start.internal, page: url.toString()} as any;

            let selector_id: string;

            if (config?.type == "ticketmachine::bundle-tickets") {
                const [{ encode }, {verifyAsync}] = await Promise.all([
                    import("@msgpack/msgpack"),
                    import("@noble/ed25519")
                ]);

                const publicKey = new Uint8Array(decodeb64(start.publicKey));
                const signature = new Uint8Array((config as any).signature);
                const tickets = new Uint8Array((config as any).tickets);

                if (!await verifyAsync(signature, tickets, publicKey)) {
                    throw new Error("Unverified signature.");
                }

                const envelope = new Blob([encode({
                    type: config.type,
                    tickets,
                    signature
                })], {type: "application/msgpack"});

                ({data: {id: selector_id}} = await client.post(replaceURLParams(urls.BundlePrepareURL, {id}), envelope, {headers: {"Content-Type": "application/msgpack"}}));
            } else {
                console.warn(config);
                throw new Error("Unknown type");
            }

            const _knownEvents = new Map<number, Promise<RawEventData>>();
            function getEvent(id: number): Promise<RawEventData> {
                if (!_knownEvents.has(id)) {
                    _knownEvents.set(id, (async () => {
                        return (await client.get<RawEventData[]>(urls.ListEventsURL, {params: {id}})).data[0]!;
                    })());
                }
                return _knownEvents.get(id)!;
            }

            type IdOnly<T> = {id: T};
            type SeatedBundleItem = {
                type: "SeatedBundle",
                id: number,
                event: IdOnly<number>, 
                category: IdOnly<number>|null,
                discount: IdOnly<number>|null,
                seat: IdOnly<number>|null
            }

            type BundleItem = {type: "UnseatedBundle", event: IdOnly<number>, id: number}|SeatedBundleItem;
            let state: BundleItem[];
            while ({data: {items: state}} = await client.get(replaceURLParams(urls.BundleStateURL, {selector_id}))) {
                console.log(state);

                const allEvents = await Promise.all(state.map((i: any) => getEvent(i.event.id)));
                allEvents.sort((a: RawEventData, b: RawEventData) => new Date(a.evDate).valueOf() - new Date(b.evDate).valueOf());

                // Find out which seats are needed
                const by_event: Record<number, BundleItem[]> = {};
                const missing_by_event: Record<number, BundleItem[]> = {};
                for (let item of state) {
                    if (item.type !== "SeatedBundle") continue;
                    (by_event[item.event.id] ||= []).push(item);

                    if (item.seat !== null) continue;
                    (missing_by_event[item.event.id] ||= []).push(item);
                }

                if (Object.keys(missing_by_event).length === 0) break;

                let currentEvent: RawEventData;
                for (currentEvent of allEvents) {
                    if ((missing_by_event[currentEvent.id]||[]).length > 0) break;
                }

                const result = yield {
                    type: "seat",
                    event: currentEvent!.id,
                    what: (by_event[currentEvent!.id] as SeatedBundleItem[]).map(e => ({
                        id: e.id,
                        seat: e.seat?.id,
                        event: e.event?.id,
                        category: e.category?.id,
                        discount: e.discount?.id
                    }))
                }

                if (result.type === "select") {
                    await client.post(replaceURLParams(urls.BundleSetSeatURL, {
                        selector_id,
                        preselect_id: result.preselected,
                        seat_id: result.seat
                    }), {});
                } else if (result.type === "unselect") {
                    await client.post(replaceURLParams(urls.BundleClearSeatURL, {
                        selector_id,
                        preselect_id: result.preselected,
                    }), {});
                } else {
                    throw new Error("Unexpected type");
                }
            }

            await client.post(replaceURLParams(urls.BundleSubmitURL, {selector_id}), {});
            await updateCart();
        }
    }
}
